import { Trans } from 'react-i18next';
import type { TFunction } from 'i18next';
import formattingUtils from '../../../../Utils/formattingUtils';
import { UsageMetricKey } from '../../../../Constants/VOSS/usageMetricKey';
import { UsageStatus } from '../../../../Constants/VOSS/usageTypes';
import type { CustomerUsagePeriod } from '../../../../Constants/VOSS/usageTypes';

interface StatRowProps {
    label: string;
    value: string;
    bold?: boolean;
}

const StatRow = ({ label, value, bold }: StatRowProps) => {
    const cls = `d-flex justify-content-between w-100${bold === true ? ' font-weight-bold' : ''}`;
    return (
        <div className={cls}>
            <span>{label}</span>
            <span>{value}</span>
        </div>
    );
};

interface PeriodSubProps {
    period: CustomerUsagePeriod;
    t: TFunction;
}

const InvoiceNumbers = ({ period, t }: PeriodSubProps) => {
    if (period.currentUsage === null) {
        return (
            <div className="d-flex align-items-baseline justify-content-center gap-4">
                <span className="h1 m-0 font-weight-bold">–</span>
            </div>
        );
    }
    const unitKey = period.currentUsage === 1 ? 'vossusage.unit.invoice' : 'vossusage.unit.invoices';
    return (
        <div className="d-flex align-items-baseline justify-content-center gap-4">
            <span className="h1 m-0 font-weight-bold">{period.currentUsage}</span>
            <span className="h3 m-0 font-weight-normal">{t(unitKey)}</span>
        </div>
    );
};

const InvoiceStats = ({ period, t }: PeriodSubProps) => {
    const price = period.unitPrice;
    const total = price !== null ? (period.currentUsage ?? 0) * price : null;
    // Unknown price must not read as "0 kr" (which would imply free).
    const sek = (value: number | null) => (value !== null ? formattingUtils.currencyFormatter(value) : '–');
    return (
        <div className="d-flex flex-column w-100 gap-8">
            <StatRow label={t('vossusage.invoiceStat.pricePerUnit')} value={sek(price)} />
            <hr className="m-0" style={{ borderColor: 'currentColor', opacity: 0.2 }} />
            <StatRow label={t('vossusage.invoiceStat.total')} value={sek(total)} bold />
        </div>
    );
};

const AccountingStats = ({ period, t }: PeriodSubProps) => {
    // No included limit means an unlimited package — the included/overage breakdown does not apply.
    if (period.includedQuantity === null) {
        return <p className="m-0">{t('vossusage.helper.unlimited')}</p>;
    }
    const included = period.includedQuantity;
    const price = period.unitPrice;
    const over = Math.max(0, (period.currentUsage ?? 0) - included);
    const extra = price !== null ? over * price : null;
    // Unknown price must not read as "0 kr/pc" (which would imply free).
    const sek = (value: number | null) => (value !== null ? formattingUtils.currencyFormatter(value) : '–');
    return (
        <div className="d-flex flex-column w-100 gap-8">
            <StatRow
                label={t('vossusage.accountingStat.included', { count: included })}
                value={formattingUtils.currencyFormatter(0)}
            />
            <StatRow
                label={t('vossusage.accountingStat.excluded', { price: sek(price) })}
                value={
                    price !== null
                        ? t('vossusage.accountingStat.excludedValue', { count: over, cost: sek(extra) })
                        : '–'
                }
            />
            <hr className="m-0" style={{ borderColor: 'currentColor', opacity: 0.2 }} />
            <StatRow label={t('vossusage.accountingStat.totalExtra')} value={sek(extra)} bold />
        </div>
    );
};

const DefaultNumbers = ({ period }: { period: CustomerUsagePeriod }) => {
    const hasLimit = period.includedQuantity !== null;
    return (
        <div className="d-flex align-items-baseline justify-content-center gap-4">
            <span className="h1 m-0 font-weight-bold">{period.currentUsage ?? '–'}</span>
            {hasLimit && (
                <span className="h3 m-0 font-weight-normal">/ {period.includedQuantity}</span>
            )}
        </div>
    );
};

interface DefaultHelperTextProps {
    period: CustomerUsagePeriod;
    status: UsageStatus;
    remaining: number | null;
    t: TFunction;
}

const DefaultHelperText = ({ period, status, remaining, t }: DefaultHelperTextProps) => {
    if (period.includedQuantity === null) {
        return <p className="m-0">{t('vossusage.helper.unlimited')}</p>;
    }
    if (status === UsageStatus.Over) {
        const over = Math.max(0, (period.currentUsage ?? 0) - period.includedQuantity);
        const extraCost = period.unitPrice !== null ? over * period.unitPrice : 0;
        return (
            <p className="m-0">
                <Trans
                    i18nKey="vossusage.helper.over"
                    values={{
                        over,
                        price: formattingUtils.currencyFormatter(period.unitPrice ?? 0),
                        extra: formattingUtils.currencyFormatter(extraCost),
                    }}
                />
            </p>
        );
    }
    return (
        <p className="m-0">
            <Trans
                i18nKey="vossusage.helper.remaining"
                values={{
                    remaining: remaining ?? 0,
                    price: formattingUtils.currencyFormatter(period.unitPrice ?? 0),
                }}
            />
        </p>
    );
};

interface UsageMetricBodyProps {
    period: CustomerUsagePeriod;
    metricKey: UsageMetricKey;
    status: UsageStatus;
    remaining: number | null;
    t: TFunction;
}

const UsageMetricBody = ({ period, metricKey, status, remaining, t }: UsageMetricBodyProps) => {
    const isInvoices = metricKey === UsageMetricKey.CustomerInvoices;
    const isAccounting = metricKey === UsageMetricKey.AutomaticAccountingAssistance;
    const hasUsage = period.currentUsage !== null;

    return (
        <>
            {isInvoices ? <InvoiceNumbers period={period} t={t} /> : <DefaultNumbers period={period} />}
            {isInvoices && hasUsage && <InvoiceStats period={period} t={t} />}
            {isAccounting && hasUsage && <AccountingStats period={period} t={t} />}
            {!isInvoices && !isAccounting && hasUsage && (
                <div className="d-flex align-items-center flex-grow-1">
                    <DefaultHelperText period={period} status={status} remaining={remaining} t={t} />
                </div>
            )}
        </>
    );
};

export default UsageMetricBody;
